import {
  RegistryId,
  Registry,
} from '@/react/portainer/registries/types/registry';
import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';

import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Link } from '@@/Link';

interface Props {
  value: RegistryId | null;
  onChange: (value: RegistryId | null) => void;
  /** Filter function to control which registries are shown */
  filter?: (registries: Array<Registry>) => Array<Registry>;
  /** Whether the selection can be cleared */
  isClearable?: boolean;
  /** Placeholder text for the select */
  placeholder?: string;
  /** Data-cy attribute for testing */
  'data-cy'?: string;
}

/**
 * A reusable registry selector component that fetches registries
 * and displays them in a select dropdown.
 */
export function RegistrySelector({
  value,
  onChange,
  filter = (registries) => registries,
  isClearable = false,
  placeholder = 'Select a registry',
  'data-cy': dataCy = 'registry-select',
}: Props) {
  const registryOptionsQuery = useRegistries<Option<RegistryId>[]>({
    select: (registries) =>
      filter(registries).map((r) => ({ label: r.Name, value: r.Id })),
  });

  const hasNoRegistries =
    !registryOptionsQuery.isLoading &&
    registryOptionsQuery.data !== undefined &&
    registryOptionsQuery.data.length === 0;

  return (
    <>
      {hasNoRegistries && (
        <TextTip color="blue" inline={false} className="mb-2">
          No registries available.{' '}
          <Link
            to="portainer.registries.new"
            target="_blank"
            rel="noopener noreferrer"
            data-cy="add-registry-link"
          >
            Add a registry
          </Link>{' '}
          to get started.
        </TextTip>
      )}
      <PortainerSelect<RegistryId>
        value={value ?? 0}
        onChange={(selectedValue) => onChange(selectedValue || null)}
        options={registryOptionsQuery.data || []}
        placeholder={placeholder}
        bindToBody
        data-cy={dataCy}
        isLoading={registryOptionsQuery.isLoading}
        isClearable={isClearable}
      />
    </>
  );
}
